import _ from 'lodash'

import type { DigitalProduct } from '@/types/digitalProduct'

type AddInfo = {
  id: number | string
  name?: string
  price_ids?: number[]
  meta?: {
    kind?: string
    options?: string[]
    multiple_choice_is_multiple?: boolean
  }
}

type AddInfoAnswer = {
  id?: number | string
  name?: string
  kind?: string
  value?: unknown
  values?: unknown[]
  values_object?: Record<string, unknown>
}

type UserTicket = {
  id: number
  price_id: number
  additional_informations?: AddInfoAnswer[] | Record<string, AddInfoAnswer>
}

const answerMergeKey = (answer: AddInfoAnswer) => {
  if (answer?.id != null) return `id:${String(answer.id)}`
  if (answer?.name) return `name:${answer.name}`
  return null
}

export const normalizeAddInfoAnswers = (
  answers: UserTicket['additional_informations']
): AddInfoAnswer[] => {
  if (!answers) return []
  if (Array.isArray(answers)) {
    return answers.filter(
      answer => answer != null && _.isPlainObject(answer)
    ) as AddInfoAnswer[]
  }
  if (_.isPlainObject(answers)) {
    return Object.entries(answers)
      .filter(([, answer]) => answer != null && _.isPlainObject(answer))
      .map(([key, answer]) => {
        const row = answer as AddInfoAnswer
        if (row.id != null) return row
        const numericKey = /^\d+$/.test(key) ? Number(key) : key
        return { ...row, id: row.id ?? numericKey }
      }) as AddInfoAnswer[]
  }
  return []
}

export const resolveAddInfoKind = (
  addInfo: AddInfo,
  addInfoAnswer?: AddInfoAnswer
) =>
  _.get(addInfo, 'meta.kind') ||
  _.get(addInfo, 'kind') ||
  _.get(addInfoAnswer, 'kind')

export const getAddInfoAnswerRecord = (
  answers: UserTicket['additional_informations'],
  addInfoId: number | string,
  addInfoName?: string
): AddInfoAnswer | undefined => {
  const targetId = String(addInfoId)
  const list = normalizeAddInfoAnswers(answers)

  const byId = list.find(
    answer => answer?.id != null && String(answer.id) === targetId
  )
  if (byId) return byId

  if (addInfoName) {
    return list.find(answer => answer?.name === addInfoName)
  }

  return undefined
}

export const getAddInfosByPriceId = ({
  digitalProduct,
  priceId,
  onlyRequired = false
}: {
  digitalProduct: DigitalProduct
  priceId: number | string
  onlyRequired?: boolean
}): AddInfo[] => {
  const allAddInfos = _.sortBy(
    _.get(digitalProduct, 'additional_informations', []),
    'position'
  ) as AddInfo[]

  const normalizedPriceId = String(priceId)

  return allAddInfos.filter(({ price_ids: priceIds, meta }) => {
    if (onlyRequired && !_.get(meta, 'options', []).includes('is_required')) {
      return false
    }

    const forPriceIds =
      priceIds &&
      priceIds.length > 0 &&
      _.get(meta, 'options', []).includes('for_price_ids')

    if (forPriceIds) {
      return priceIds.some(pid => String(pid) === normalizedPriceId)
    }
    return true
  })
}

const isGlobalAddInfo = (addInfo: AddInfo) => {
  const priceIds = addInfo.price_ids || []
  const forPriceIds = _.get(addInfo, 'meta.options', []).includes(
    'for_price_ids'
  )
  return !forPriceIds || priceIds.length === 0
}

/** Alinhado à checagem legada do AddInfoSurvey (valor, lista ou endereço). */
export const hasAnyAnswerContent = (
  addInfoAnswer: AddInfoAnswer | undefined
): boolean => {
  if (!addInfoAnswer) return false

  const { value, values, values_object: valuesObject } = addInfoAnswer

  if (_.isPlainObject(valuesObject) && !_.isEmpty(valuesObject)) return true
  if (_.isArray(values) && values.length > 0) return true
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return true
  }
  if (typeof value === 'string') return value.trim().length > 0
  if (value != null && value !== false) return true

  return false
}

export const hasAnswerPayload = (addInfoAnswer: AddInfoAnswer | undefined) => {
  if (!addInfoAnswer) return false

  const valuesObject = addInfoAnswer.values_object
  if (_.isPlainObject(valuesObject) && !_.isEmpty(valuesObject)) {
    return true
  }

  const values = addInfoAnswer.values
  if (_.isArray(values) && values.length > 0) {
    return true
  }

  const value = addInfoAnswer.value
  if (value == null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.length > 0
  if (_.isPlainObject(value)) return !_.isEmpty(value)
  return !!value
}

export const isAddInfoAnswerFilled = (
  addInfo: AddInfo,
  addInfoAnswer: AddInfoAnswer | undefined
): boolean => {
  if (!addInfoAnswer) return false

  const kind = resolveAddInfoKind(addInfo, addInfoAnswer)

  if (kind === 'address') {
    return hasAnswerPayload(addInfoAnswer)
  }

  if (kind === 'multiple_choice') {
    const multipleChoiceIsMultiple = _.get(
      addInfo,
      'meta.multiple_choice_is_multiple'
    )
    if (multipleChoiceIsMultiple) {
      return (
        _.isArray(addInfoAnswer.values) && addInfoAnswer.values.length > 0
      )
    }
    return hasAnswerPayload(addInfoAnswer)
  }

  if (kind === 'term') {
    return (
      addInfoAnswer.value === true ||
      addInfoAnswer.value === 'true' ||
      addInfoAnswer.value === 1 ||
      addInfoAnswer.value === '1'
    )
  }

  return hasAnswerPayload(addInfoAnswer) || hasAnyAnswerContent(addInfoAnswer)
}

/** Resposta em qualquer ingresso do usuário (campos globais sem for_price_ids). */
export const findFilledAnswerAcrossTickets = (
  tickets: UserTicket[] | undefined,
  addInfo: AddInfo
): AddInfoAnswer | undefined => {
  if (!tickets?.length) return undefined

  for (const ticket of tickets) {
    const answer = getAddInfoAnswerRecord(
      ticket.additional_informations,
      addInfo.id,
      addInfo.name
    )
    if (isAddInfoAnswerFilled(addInfo, answer)) return answer
  }

  return undefined
}

export const isRequiredAddInfoUnanswered = (
  addInfo: AddInfo,
  ticket: UserTicket,
  tickets: UserTicket[] | undefined
) => {
  let answer = getAddInfoAnswerRecord(
    ticket.additional_informations,
    addInfo.id,
    addInfo.name
  )

  if (!isAddInfoAnswerFilled(addInfo, answer) && isGlobalAddInfo(addInfo)) {
    const shared = findFilledAnswerAcrossTickets(tickets, addInfo)
    if (shared) answer = shared
  }

  return !isAddInfoAnswerFilled(addInfo, answer)
}

export const mergeTicketAdditionalInformations = (
  existing: UserTicket['additional_informations'],
  submitted: UserTicket['additional_informations']
): AddInfoAnswer[] => {
  const merged = new Map<string, AddInfoAnswer>()

  for (const answer of [
    ...normalizeAddInfoAnswers(existing),
    ...normalizeAddInfoAnswers(submitted)
  ]) {
    const key = answerMergeKey(answer)
    if (!key) continue
    merged.set(key, { ...merged.get(key), ...answer })
  }

  return Array.from(merged.values())
}

export const findTicketWithUnansweredRequiredAddInfos = ({
  digitalProduct,
  tickets
}: {
  digitalProduct: DigitalProduct
  tickets: UserTicket[] | undefined
}): UserTicket | null => {
  if (!tickets?.length) return null

  return (
    tickets.find(ticket => {
      const requiredAddInfos = getAddInfosByPriceId({
        digitalProduct,
        priceId: ticket.price_id,
        onlyRequired: true
      })

      if (!requiredAddInfos.length) return false

      return requiredAddInfos.some(addInfo =>
        isRequiredAddInfoUnanswered(addInfo, ticket, tickets)
      )
    }) ?? null
  )
}

export const ticketHasUnansweredRequiredAddInfos = ({
  digitalProduct,
  ticket,
  tickets
}: {
  digitalProduct: DigitalProduct
  ticket: UserTicket
  tickets?: UserTicket[]
}): boolean => {
  const requiredAddInfos = getAddInfosByPriceId({
    digitalProduct,
    priceId: ticket.price_id,
    onlyRequired: true
  })

  if (!requiredAddInfos.length) return false

  return requiredAddInfos.some(addInfo =>
    isRequiredAddInfoUnanswered(addInfo, ticket, tickets)
  )
}

export const getAddInfoAnswerValue = (
  addInfoAnswer: AddInfoAnswer | undefined,
  addInfo: AddInfo
) => {
  if (!addInfoAnswer) return null

  const kind = resolveAddInfoKind(addInfo, addInfoAnswer)
  const multipleChoiceIsMultiple = _.get(
    addInfo,
    'meta.multiple_choice_is_multiple'
  )

  if (kind === 'address') return addInfoAnswer.values_object
  if (kind === 'multiple_choice' && multipleChoiceIsMultiple) {
    return addInfoAnswer.values
  }
  return addInfoAnswer.value
}

/** Home route: /{slug} or / on custom gamification domain */
export const isDigitalProductHomePath = (
  pathname: string,
  basePathWithId: string
): boolean => {
  const normalizedPath = (pathname || '/').replace(/\/$/, '') || '/'
  const base = (basePathWithId || '').replace(/\/$/, '')

  if (base) {
    return normalizedPath === base
  }

  const segments = normalizedPath.split('/').filter(Boolean)
  return segments.length === 0
}
